import { axiosInstance } from "@/providers/data";

/**
 * Evidence holds — `GET/POST /api/v2/admin/evidence/…` (immersive-server,
 * plans/compliance-launch/ncmec.md §10). The shapes here mirror the server's
 * `EvidenceHoldListItem` / `EvidenceHoldDetail`: ids, clocks, decisions and
 * sizes. Never a path, a URL or the material itself — that only ever arrives
 * as bytes, one object at a time, from `fetchEvidenceBlob`.
 */
export const EVIDENCE_RESOURCE = "admin/evidence/holds";

export type EvidenceHoldStatus = "active" | "reported" | "released";
export type EvidencePreserveState = "pending" | "preserved" | "failed";
export type EvidenceReviewOutcome = "apparent_csam" | "not_csam" | "needs_counsel";
// `generated_classifier`: the server's output check held a picture the Studio generated, as an apparent minor
// (immersive-server plans/compliance-launch/imx-explicit.md §4.6). It was never shown; reporting it is the
// reviewer's decision here, never automatic.
export type EvidenceTrigger =
  | "upload_classifier"
  | "user_report"
  | "removal_request"
  | "manual"
  | "legal_process"
  | "generated_classifier";
export type EvidenceTargetType =
  | "upload"
  | "story"
  | "character"
  | "comment"
  | "message"
  | "creator"
  | "v2_entry"
  | "v2_comment"
  | "v2_message"
  | "v2_character"
  | "v2_generated";
export type EvidenceExtensionBasis = "legal_process" | "voluntary";
export type EvidenceAction =
  | "placed"
  | "preserved"
  | "preserve_failed"
  | "unpublished"
  | "cdn_purged"
  | "viewed"
  | "reviewed"
  | "reported"
  | "extended"
  | "released"
  | "copies_deleted"
  | "note"
  | "cybertipline_started"
  | "cybertipline_opened"
  | "cybertipline_finishing"
  | "cybertipline_sent"
  | "cybertipline_failed"
  | "cybertipline_unresolved";

export type EvidenceHoldListItem = {
  publicId: string;
  trigger: EvidenceTrigger;
  status: EvidenceHoldStatus;
  preserveState: EvidencePreserveState;
  targetType: EvidenceTargetType;
  targetRef: string;
  subjectUserId: number | null;
  minorScore: number | null;
  // The picture check's own (a generated picture, or the creator's own sent to a story); null on an upload's check.
  // Optional: a server from before them sends none.
  explicitScore?: number | null;
  ageAmbiguousScore?: number | null;
  framing?: HeldFraming | null;
  objectCount: number;
  contentReportId: number | null;
  removalRequestId: number | null;
  reviewOutcome: EvidenceReviewOutcome | null;
  ncmecReportId: string | null;
  placedAt: string;
  reviewedAt: string | null;
  reportedAt: string | null;
  preserveUntil: string | null;
  releasedAt: string | null;
  /** The 24-hour clock, while the hold still waits for an outcome. */
  reviewDueAt: string | null;
};

export type EvidenceObjectSummary = {
  index: number;
  contentType: string | null;
  sizeBytes: number;
};

export type EvidenceSubjectSnapshot = {
  userId: number;
  email: string;
  name: string;
  accountCreatedAt: string;
  supertokensUserId: string | null;
  ageBand: "adult" | "minor" | "under_13" | null;
  capturedAt: string;
};

export type EvidenceClassifier = {
  verdict: string;
  provider: string;
  coverage: string;
  checkedAt: string;
  categories?: {
    sexual: number;
    violence: number;
    minor: number;
    nonConsent: number;
    realPerson: number;
    hate: number;
    // The picture check's own, on a generated picture's hold only.
    ageAmbiguous?: number;
    animal?: number;
  };
  reason?: string;
};

// How the picture check framed the apparent minor it held (immersive-server src/services/held-scores.ts). Its
// "minor" score is an apparent under-18 in a sexual OR suggestive framing, and its "explicit" score is nudity or sex
// acts, so a hit is one of two: explicit, or suggestive and not explicit. Never "no sexual content": nothing scores that.
export type HeldFraming = "explicit" | "suggestive";

// A hold's scores as a report queue row carries them (`heldScores`). `explicit` and `ageAmbiguous` are the picture
// check's own: null on an upload's check, which scores "sexual" on another scale.
export type HeldScores = {
  minor: number | null;
  ageAmbiguous: number | null;
  explicit: number | null;
  framing: HeldFraming | null;
};

export const FRAMING_LABEL: Record<HeldFraming, string> = {
  explicit: "Apparent minor · explicit",
  suggestive: "Apparent minor · suggestive, not explicit",
};

/** A classifier score as the panel prints it: "-" when there is none. */
export const formatScore = (score: number | null | undefined): string =>
  score === null || score === undefined ? "-" : score.toFixed(2);

export type EvidenceEventDetail = {
  objects?: number;
  objectIndex?: number;
  reason?: string;
  outcome?: EvidenceReviewOutcome;
  note?: string;
  ncmecReportId?: string;
  submittedAt?: string;
  until?: string;
  basis?: EvidenceExtensionBasis;
  reference?: string;
  contentReportId?: number;
  removalRequestId?: number;
  /** A takedown was asked for with this hold; the sweep finishes one that did not complete. */
  unpublish?: boolean;
};

export type EvidenceHoldEvent = {
  id: number;
  holdId: number;
  action: EvidenceAction;
  actorId: number | null;
  detail: EvidenceEventDetail | null;
  at: string;
};

export type EvidenceHoldDetail = EvidenceHoldListItem & {
  subjectSnapshot: EvidenceSubjectSnapshot | null;
  textSnapshot: string | null;
  classifier: EvidenceClassifier | null;
  objects: EvidenceObjectSummary[];
  placedBy: number | null;
  reviewedBy: number | null;
  reportedBy: number | null;
  releasedBy: number | null;
  releaseReason: string | null;
  /** Why release is refused right now, or null when it is allowed (ncmec.md §10.5). */
  releaseBlockedBy: string | null;
  /** Whether the server can file the report through NCMEC's Reporting API; when not, it is filed by hand. */
  cybertiplineAvailable: boolean;
  events: EvidenceHoldEvent[];
};

export const OUTCOME_LABEL: Record<EvidenceReviewOutcome, string> = {
  apparent_csam: "Apparent CSAM",
  not_csam: "Not CSAM",
  needs_counsel: "Needs counsel",
};

export const TRIGGER_LABEL: Record<EvidenceTrigger, string> = {
  upload_classifier: "Upload classifier",
  user_report: "User report",
  removal_request: "Removal request",
  manual: "Placed by hand",
  legal_process: "Legal process",
  generated_classifier: "Generated-picture check",
};

export const TARGET_LABEL: Record<EvidenceTargetType, string> = {
  upload: "Upload",
  story: "Story",
  character: "Character",
  comment: "Comment",
  message: "Message",
  creator: "Account",
  v2_entry: "Entry (v2)",
  v2_comment: "Comment (v2)",
  v2_message: "Line (v2)",
  v2_character: "Character (v2)",
  v2_generated: "Generated picture (v2)",
};

export const ACTION_LABEL: Record<EvidenceAction, string> = {
  placed: "Hold placed",
  preserved: "Copies verified and held",
  preserve_failed: "Preservation failed",
  unpublished: "Removed from the media bucket",
  cdn_purged: "CDN purge requested",
  viewed: "Material viewed",
  reviewed: "Outcome recorded",
  reported: "CyberTipline report recorded",
  extended: "Preservation extended",
  released: "Released",
  copies_deleted: "Copies deleted",
  note: "Note",
  cybertipline_started: "CyberTipline filing started",
  cybertipline_opened: "CyberTipline report opened",
  cybertipline_finishing: "CyberTipline report being sent",
  cybertipline_sent: "CyberTipline report sent, files included",
  cybertipline_failed: "CyberTipline filing failed, nothing sent",
  cybertipline_unresolved: "CyberTipline answer lost: check NCMEC's portal, then record the report",
};

const HOUR_MS = 60 * 60 * 1000;

/** The 24-hour review clock, against the precise deadline: red once breached, amber inside six hours. */
export const reviewClock = (
  dueAt: string,
  now: number = Date.now()
): { color: "error" | "warning" | "default"; label: string } => {
  const msLeft = new Date(dueAt).getTime() - now;
  if (Number.isNaN(msLeft)) {
    return { color: "default", label: "-" };
  }
  if (msLeft <= 0) {
    const overdue = -msLeft;
    return {
      color: "error",
      label: overdue < HOUR_MS ? `${Math.ceil(overdue / 60_000)}m overdue` : `${Math.floor(overdue / HOUR_MS)}h overdue`,
    };
  }
  return {
    color: msLeft < 6 * HOUR_MS ? "warning" : "default",
    label: msLeft < HOUR_MS ? `${Math.ceil(msLeft / 60_000)}m left` : `${Math.floor(msLeft / HOUR_MS)}h left`,
  };
};

const ACCESS_TTL_MS = 60_000;
let accessCheck: { at: number; result: Promise<boolean> } | null = null;

/** Forget the cached answer. The auth provider calls this whenever the signed-in admin changes. */
export function forgetEvidenceAccess(): void {
  accessCheck = null;
}

/**
 * Whether the signed-in admin is on the server's reviewer list. `GET access`
 * answers 204 or 403; an admin role alone is not enough. Cached for a minute
 * so the menu does not ask on every render.
 */
export function canReviewEvidence(): Promise<boolean> {
  if (accessCheck && Date.now() - accessCheck.at < ACCESS_TTL_MS) {
    return accessCheck.result;
  }
  const result = axiosInstance.get("/api/v2/admin/evidence/access").then(
    () => true,
    () => false
  );
  accessCheck = { at: Date.now(), result };
  return result;
}

/**
 * One preserved object, as bytes, with the session's own auth header. The
 * request is what the server records as a view; the caller turns the bytes
 * into a memory-only object URL and revokes it. Nothing here produces a link
 * that could be pasted anywhere.
 */
export async function fetchEvidenceBlob(publicId: string, index: number): Promise<Blob> {
  const response = await axiosInstance.get<Blob>(
    `/api/v2/${EVIDENCE_RESOURCE}/${encodeURIComponent(publicId)}/objects/${index}`,
    { responseType: "blob" }
  );
  return response.data;
}
